import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './AdminDashboard.css';
import api from '../api';

// Define the sections you want to manage
const sections = [
    {
        key: 'supplier',
        label: 'Suppliers',
        endpoint: 'supplier/admin',
        columns: ['name', 'StreetAddress', 'City', 'Province', 'PostalCode', 'phone', 'website', 'logoURL', 'isActive'],
        allowAdd: false,
        allowEdit: true,
        allowDelete: false
    },

    {
        key: 'announcements',
        label: 'Announcements',
        endpoint: '/announcements/admin',
        columns: ['title', 'description', 'startDate', 'endDate', 'isActive', 'CreatedBy', 'ChangedBy'],
        largeFields: ['description'],
        allowAdd: true,
        allowEdit: true,
        allowDelete: true,
        detail: {
            detailEndpoint: '/announcements/admin'
        }
    },
    {
        key: 'seasonalTips',
        label: 'Seasonal Tips',
        endpoint: '/seasonalTips/admin',
        columns: ['title', 'description', 'startDate', 'endDate', 'isActive', 'CreatedBy', 'ChangedBy'],
        largeFields: ['description'],
        allowAdd: true,
        allowEdit: true,
        allowDelete: true,
                detail: {
            detailEndpoint: '/seasonalTips/admin'
        }

    },
    {
        key: 'gardenTypes',
        label: 'Garden Types',
        endpoint: '/garden-types',
        columns: ['name', 'description', 'code'],
        largeFields: ['description'],
        allowAdd: true,
        allowEdit: true,
        allowDelete: true
    },
    {
        key: 'locations',
        label: 'Locations',
        endpoint: '/locations/admin',
        columns: ['city', 'province', 'country', 'weatherStation','regionID', 'longitude', 'latitude'],
        allowAdd: true,
        allowEdit: true,
        allowDelete: false
    },
    {
        key: 'users',
        label: 'Users',
        endpoint: '/users/admin',
        columns: ['username'],
        allowAdd: false,
        allowEdit: false,
        allowDelete: true
     },
     {
        key: 'plantNotes',
        label: 'Plant Notes',
        endpoint: '/plants/admin/plantNotes',
        columns: ['plantID', 'plant', 'experienceLevel', 'note'],
        largeFields: ['note'],
        allowAdd: false,
        allowEdit: true,
        allowDelete: false
    },

     {
        key: 'germinationNotes',
        label: 'Germination Notes',
        endpoint: '/plants/admin/germNotes',
        columns: ['plant', 'experienceLevel', 'note'],
        largeFields: ['note'],
        allowAdd: false,
        allowEdit: true,
        allowDelete: false
    },

     {
        key: 'thinningNotes',
        label: 'Thinning Notes',
        endpoint: '/plants/admin/thinNotes',
        columns: ['plant', 'experienceLevel', 'note'],
        largeFields: ['note'],
        allowAdd: false,
        allowEdit: true,
        allowDelete: false
    },

     {
        key: 'harvestNotes',
        label: 'Harvest Notes',
        endpoint: '/plants/admin/harvestNotes',
        columns: ['plant', 'experienceLevel', 'note'],
        largeFields: ['note'],
        allowAdd: false,
        allowEdit: true,
        allowDelete: false
    },

    {
        key: 'gardeners',
        label: 'Gardeners',
        endpoint: '/gardeners/admin',
        columns: ['name', 'isdemo', 'isadmin'],
        allowAdd: false,
        allowEdit: true,
        allowDelete: false
    }
    // add more as needed
];

const GenericDetailTableSection = ({ masterId, detail }) => {
  const [allDetails, setAllDetails] = useState([]);
  const [linkedDetails, setLinkedDetails] = useState([]);


  // Fetch all possible details
  useEffect(() => {
    if (!detail || !masterId) return;
    const fetchAll = async () => {
      try {
        const res = await api.get(`${detail.detailEndpoint}/${masterId}/cities/all`);
        setAllDetails(res.data || []);
      } catch (err) {
        console.error('Error fetching all details:', err);
      }
    };
    fetchAll();
  }, [detail, masterId]);

  // Fetch linked details
  useEffect(() => {
    if (!detail || !masterId) return;
    const fetchLinked = async () => {
      try {
        const res = await api.get(`${detail.detailEndpoint}/${masterId}/cities`);
        setLinkedDetails(res.data || []);
      } catch (err) {
        console.error('Error fetching linked details:', err);
      }
    };
    fetchLinked();
  }, [detail, masterId]);

  const handleLinkUnlink = async (detailId, checked) => {
    try {
      if (checked) {
        await api.post(`${detail.detailEndpoint}/${masterId}/cities/link`, { detailId });
      } else {
        await api.delete(`${detail.detailEndpoint}/${masterId}/cities/unlink/${detailId}`);
      }
      const res = await api.get(`${detail.detailEndpoint}/${masterId}/cities`);
      setLinkedDetails(res.data || []);
    } catch (err) {
      console.error('Error updating detail link:', err);
    }
  };

  if (!masterId) return null;

  return (
    <div className="detail-list">
      <h5>Linked Cities</h5>
      <ul>
        {allDetails.map(d => {
          const isLinked = linkedDetails.some(ld => ld.id === d.id);
          return (
            <li key={d.id}>
              <label>
                <input
                  type="checkbox"
                  checked={isLinked}
                  onChange={(e) => handleLinkUnlink(d.id, e.target.checked)}
                />
                {d.name}
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const GenericTableSection = ({ section }) => {
    const { endpoint, columns, label, allowAdd, allowEdit, allowDelete, onResetPassword } = section;
    const [rows, setRows] = useState([]);
    const [dirtyCells, setDirtyCells] = useState({});
    const [status, setStatus] = useState('');
    const [selectedRowId, setSelectedRowId] = useState(null);

  const isLargeField = (field) =>
    section.largeFields && section.largeFields.includes(field);


    const fetchRows = async () => {
        try {
            const res = await api.get(endpoint);

            const converted = res.data.map(row => {
                const newRow = { ...row };

                // Convert any field containing "date" into a Date object
                Object.keys(newRow).forEach(key => {
                    if (key.toLowerCase().includes('date') && newRow[key]) {
                        newRow[key] = new Date(newRow[key]);
                    }
                });

                return newRow;
            });

            setRows(converted);
            setDirtyCells({});
        } catch (err) {
            console.error(`Error fetching ${endpoint}:`, err);
        }
    };

    useEffect(() => {
        fetchRows();
    }, [endpoint]);

    const handleCellChange = (rowID, col, value) => {
        setRows(prev =>
            prev.map(row =>
                row.id === rowID ? { ...row, [col]: value } : row
            )
        );
        setDirtyCells(prev => ({ ...prev, [`${rowID}|${col}`]: true }));
    };

    const handleAddRow = () => {
        const newRow = { id: Date.now(), isTemporary: true };
        columns.forEach(col => {
            newRow[col] = col === 'isActive' ? false : '';
        });
        setRows(prev => [...prev, newRow]);
        setDirtyCells(prev => ({ ...prev, [`${newRow.id}|new`]: true }));
        };

const handleSubmitChanges = async () => {
  const updates = rows.filter(r =>
    Object.keys(dirtyCells).some(key => key.startsWith(`${r.id}|`))
  );

  if (updates.length === 0) {
    setStatus('No changes to submit.');
    return;
  }

  try {
    for (const row of updates) {
      // Build payload dynamically from the section's columns
      const payload = {};
      columns.forEach(col => {
        payload[col] = row[col];
      });

      if (row.isTemporary) {
        await api.post(endpoint, payload);
      } else {
        await api.put(`${endpoint}/${row.id}`, payload);
      }
    }

    setStatus('Changes submitted.');
    setDirtyCells({});
    fetchRows();
  } catch (err) {
    console.error('Error submitting changes:', err);
    setStatus('Error submitting changes.');
  }
};

    const handleDelete = async (id) => {
        try {
            await api.delete(`${endpoint}/${id}`);
            setStatus('Row deleted.');
            fetchRows();
        } catch (err) {
            console.error(`Error deleting row from ${endpoint}:`, err);
            setStatus('Error deleting row.');
        }
    };

    const renderInput = (rowID, col, value) => {

        if (isLargeField(col)) {
            return (
                <span
                    className="clickable-note"
                    onClick={(e) => {
                        e.stopPropagation();
                        openLargeEditor(rows.find(r => r.id === rowID), col);
                    }}
                >
                    {value ? value.substring(0, 60) + (value.length > 60 ? "�" : "") : ""}
                </span>
            );
        }


        if (!allowEdit) {
            return <span>{value}</span>;  // read-only
        }

        if (col === 'isActive') {
            return (
                <input
                    type="checkbox"
                    checked={!!value}
                    onChange={(e) => handleCellChange(rowID, col, e.target.checked)}
                />
            );
        }
        if (col.toLowerCase().includes('date')) {
            return (
                <DatePicker
                    selected={value ? new Date(value) : null}
                    onChange={(date) => {
                        const isoDate = date.toISOString().split('T')[0];
                        handleCellChange(rowID, col, isoDate);
                    }}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select date"
                    className="calendar-input"
                />
            );
        }
        return (
            <input
                type="text"
                value={value || ''}
                onChange={(e) => handleCellChange(rowID, col, e.target.value)}
            />
        );
    };

    const [largeEditor, setLargeEditor] = useState(null);

    const openLargeEditor = (row, field) => {
        setLargeEditor({
            row,
            field,
            value: row[field]
        });
    };

    const closeLargeEditor = () => setLargeEditor(null);

    const saveLargeEditor = async () => {
        await api.put(`${section.endpoint}/${largeEditor.row.id}`, {
            ...largeEditor.row,
            [largeEditor.field]: largeEditor.value
        });
        closeLargeEditor();
        fetchRows();
    };


    return (
        <div className="generic-table-section">
            <h4>Currently Editing: {label}</h4>
            <div className="matrix-grid">
                <div className="matrix-columns header-row">
                    {columns.map(col => (
                        <div key={col} className="matrix-column">{col}</div>
                    ))}
                    {allowDelete && <div className="matrix-column">Actions</div>}
                </div>

                {rows.map(row => (
                    <div 
                        key={row.id}
                        className={`matrix-columns data-row ${selectedRowId === row.id ? 'selected' : ''}`}
                        onClick={() => setSelectedRowId(row.id)} 
                     >

                    {columns.map(col => (
                        <div key={col} className="matrix-column">
                            {isLargeField(col) ? (
                                <span
                                    className="clickable-note"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openLargeEditor(row, col);
                                    }}
                                >
{(row[col] || "").trim()
    ? (row[col].trim().substring(0, 60) + (row[col].trim().length > 60 ? "�" : ""))
    : "[click to edit]"}
                                </span>
                            ) : (
                                renderInput(row.id, col, row[col])
                            )}
                        </div>
                    ))}

                        <div className="matrix-column">
                        {allowDelete && (
                                <button
                                    onClick={() => handleDelete(row.id)}
                                    className="button small danger"
                                >
                                    Delete
                                </button>
                        )}

                        {section.key === 'supplier' && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation(); 
                                    console.log("ROW ID BEING PASSED IN: ", row.id)
                                    if (section.onResetPassword) {
                                        section.onResetPassword(row.id);
                                    }
                                }}
                                className="button small"
                            >
                                Reset Password
                            </button>
                        )}

                    </div>
                    </div>
                ))}
            </div>

            {section.detail && selectedRowId && rows.some(r => r.id === selectedRowId && !r.isTemporary) && (
                <GenericDetailTableSection
                            masterId={selectedRowId}
                            detail={section.detail}
                            />
                        )}

                        <div className="table-actions">
                            {allowAdd && <button onClick={handleAddRow} className="button">Add Row</button>}
                            {allowEdit && <button onClick={handleSubmitChanges} className="button">Submit Changes</button>}
                            {status && <p className="status-message">{status}</p>}
                        </div>

                        {largeEditor && (
                            <div className="modal-backdrop">
                                <div className="modal">
                                    <h3>Edit {largeEditor.field}</h3>

                                    <textarea
                                        rows={12}
                                        value={largeEditor.value}
                                        onChange={(e) =>
                                            setLargeEditor({ ...largeEditor, value: e.target.value })
                                        }
                                    />

                                    <div className="modal-actions">
                                        <button className="button" onClick={saveLargeEditor}>Save</button>
                                        <button className="button secondary" onClick={closeLargeEditor}>Cancel</button>
                                    </div>
                                </div>
                            </div>
                        )}


                    </div>
                );
            };


const AdminDashboard = ({ currentGardener, token, handleTabChange }) => {
    const [selectedSection, setSelectedSection] = useState(sections[0]);
    const [status, setStatus] = useState('');
    const [showSupplierForm, setShowSupplierForm] = useState(false);

    const [supplierUsername, setSupplierUsername] = useState('');
    const [supplierPassword, setSupplierPassword] = useState('');
    const [supplierName, setSupplierName] = useState('');

    const [resetPassword, setResetPassword] = useState('');
    const [resetUserID, setResetUserID] = useState(null);


    const handleRegisterSupplier = async () => {
        try {
            const res = await fetch('/api/auth/registerSupplier', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    username: supplierUsername,
                    password: supplierPassword,
                    name: supplierName
                })
            });

            const data = await res.json();
            setStatus(data.message || 'Supplier registered');

            // Reset form + hide it 
            setSupplierUsername(''); 
            setSupplierPassword(''); 
            setSupplierName('');
            setShowSupplierForm(false);

        } catch (err) {
            setStatus('Error registering supplier');
        }
    };

    const handleResetSupplierPassword = async () => {
    try {
        const res = await fetch('/api/auth/ResetSupplierPassword', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
                supplierID: resetUserID,
                password: resetPassword
            })
        });

        const data = await res.json();
        setStatus(data.message || 'Password reset');

        // Clear fields
        setResetPassword('');
        setResetUserID(null);

    } catch (err) {
        setStatus('Error resetting password');
    }
};


    return (
        <div className="admin-dashboard">

            {/* Create a New Supplier */}
            <div className="top-actions">
                <h3>Manage Suppliers</h3>
                <button className="button" onClick={() => setShowSupplierForm(true)}>
                    Register New Supplier
                </button>
                {status && <p className="status-message">{status}</p>}
            </div>

            {resetUserID && (
                <div className="supplier-form">
                    <h4>Reset Supplier Password</h4>

                    <label>New Temporary Password</label>
                    <input
                        type="text"
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                    />

                    <button className="button" onClick={handleResetSupplierPassword}>
                        Save New Password
                    </button>

                    <button
                        className="button secondary"
                        onClick={() => {
                            setResetUserID(null);
                            setResetPassword('');
                        }}
                    >
                        Cancel
                    </button>
                </div>
            )}


            {showSupplierForm && (
                <div className="supplier-form">
                    <label>Name</label>
                    <input
                        type="text"
                        value={supplierName}
                        onChange={(e) => setSupplierName(e.target.value)}
                    />

                    <label>Username</label>
                    <input
                        type="text"
                        value={supplierUsername}
                        onChange={(e) => setSupplierUsername(e.target.value)}
                    />

                    <label>Temporary Password</label>
                    <input 
                        type="text"
                        value={supplierPassword}
                        onChange={(e) => setSupplierPassword(e.target.value)}
                    />

                    <button className="button" onClick={handleRegisterSupplier}>
                        Create Supplier
                    </button>

                    <button 
                        className="button secondary" 
                        onClick={() => setShowSupplierForm(false)}
                    >
                        Cancel
                    </button>
                </div>
            )}


            {/* Existing lookup-table section */}
            <section>
                <h3>Manage Lookup Tables</h3>

                <nav className="admin-menu">
                    {sections.map(section => (
                        <button
                            key={section.key}
                            className={selectedSection.key === section.key ? 'active' : ''}
                            onClick={() => setSelectedSection(section)}
                        >
                            {section.label}
                        </button>
                    ))}
                </nav>

                <GenericTableSection 
                    section={{
                        ...selectedSection,
                        onResetPassword: (id) => setResetUserID(id)
                    }} 
                />
            </section>
        </div>
    );
};

export default AdminDashboard;
